import jwt_decode from "jwt-decode";
import { ROLES } from "./roles.js";

// Identita' dal payload dell'access token (solo lettura, nessuna verifica di
// firma). Campi emessi dal BE (login/refresh): UserInfo.id, .username, .role,
// .type. Il token di impersonazione (impersonateController.signImpersonationToken)
// porta in piu' UserInfo.impersonation = { isImpersonating, adminId, adminUsername }
// e id/username/role/type del TARGET.
export const decodeUserInfo = (token) => {
  try {
    return token ? jwt_decode(token)?.UserInfo || null : null;
  } catch {
    return null;
  }
};

export const impersonationClaim = (token) => {
  const info = decodeUserInfo(token);
  return info?.impersonation?.isImpersonating ? info.impersonation : null;
};

// Stato `impersonation.active` ricostruito dal claim (dopo F5 lo stato Redux e'
// perso, il token no). savedAdminAuth non e' recuperabile.
export const impersonationFromToken = (token) => {
  const info = decodeUserInfo(token);
  const claim = info?.impersonation;
  if (!claim?.isImpersonating) return null;
  const exp = jwt_decode(token)?.exp;
  return {
    targetUserId: info.id,
    targetUsername: info.username,
    adminId: claim.adminId,
    adminUsername: claim.adminUsername,
    expiresAt: exp ? exp * 1000 : null,
  };
};

// Identita' che il refresh DEVE restituire quando si esce da un'impersonazione.
// - savedAdminToken (in memoria): id + type dell'admin;
// - claim (dal sessionToken, anche dopo F5, o da `impersonation` = stato
//   `active` in memoria, perche' l'interceptor puo' aver gia' cancellato il
//   token da sessionStorage dopo un errore 5xx): id e username dell'admin e
//   role ADMIN (il claim non porta il type).
// null = non siamo in un contesto di impersonazione: nessun controllo.
export const expectedAdminIdentity = ({ sessionToken, savedAdminToken, impersonation }) => {
  const claim = impersonationClaim(sessionToken) || (impersonation?.adminId != null ? impersonation : null);
  const saved = decodeUserInfo(savedAdminToken);
  if (!claim && !saved) return null;
  return {
    ...(claim && {
      id: claim.adminId,
      username: claim.adminUsername,
      role: ROLES.ADMIN,
    }),
    // id + type dell'admin salvato prevalgono sul claim
    ...(saved?.id != null && { id: saved.id, type: saved.type }),
  };
};

// Ogni campo atteso deve coincidere (id e type insieme: lo stesso id puo'
// esistere in tabelle diverse, admin e clienti).
export const identityMatches = (token, expected) => {
  const info = decodeUserInfo(token);
  if (!info || !expected) return false;
  return Object.entries(expected).every(([key, value]) => info[key] === value);
};

// Forma dell'oggetto `auth` (quella costruita dal refresh) derivata dal solo
// access token: user_id, role, type, ... servono a PersistentLogin (socket,
// navigazione). Il token di impersonazione e' l'esempio tipico: ClientList
// faceva setAuth({ accessToken }) e `auth` restava senza user_id/role.
export const authDataFromToken = (accessToken) => {
  const info = decodeUserInfo(accessToken);
  if (!info) return { accessToken };
  return {
    accessToken,
    role: info.role || "",
    type: info.type,
    client_name: info.client_name || "",
    user_id: info.id || "",
    username: info.username || "",
    password_expire_date: info.password_expire_date || "",
    password_expiring: info.password_expiring ?? false,
  };
};

// Completa un `auth` letto da sessionStorage: i campi gia' presenti vincono,
// quelli mancanti (es. dopo un'impersonazione) si derivano dal token.
export const completeAuthData = (authData) => {
  if (!authData?.accessToken) return authData;
  return { ...authDataFromToken(authData.accessToken), ...authData };
};
